import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Incidencias vistas desde el panel de personal municipal (HU-004) —
 * separado de `useIncidencias.js` (que es el flujo de creación del
 * ciudadano, público). Siempre manda el JWT del personal logueado; si no
 * hay token o el rol no alcanza, el backend ya responde 401/403 real
 * (mismo mecanismo de `ROLES_STAFF_ADMIN` que usan `usuario`/`categoria`/
 * `configuracion`), así que acá no hace falta un fallback silencioso.
 */
const useIncidenciasPanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    ...extra,
  });

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.detail || 'Error inesperado del servidor');
    }
    return data;
  };

  const obtenerAlertasDelDia = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.incidencia.alertasHoy, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, alertas: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Incidencias de hoy con lat/lng, para las boyas del mapa del dashboard
  // (HU-010 Fase 3) — sin `setLoading` global: se llama en paralelo con
  // `obtenerAlertasDelDia`/`obtenerEstadisticas` en la misma pantalla y por
  // polling, no debe pisar el indicador de carga de las otras.
  const obtenerMapaHoy = async () => {
    try {
      const response = await fetch(API_CIFRA.incidencia.mapaHoy, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, incidencias: data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // `filtros`: { ticket, status, categoria, anonimo, fecha_desde, fecha_hasta, page, page_size }
  // — se mandan solo los que tengan valor, el backend ignora el resto.
  const listarIncidencias = async (filtros = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([clave, valor]) => {
        if (valor !== null && valor !== undefined && valor !== '') params.set(clave, valor);
      });
      const url = params.toString() ? `${API_CIFRA.incidencia.listar}?${params}` : API_CIFRA.incidencia.listar;

      const response = await fetch(url, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, ...data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const cambiarStatus = async (id, nuevoStatus, observaciones = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.incidencia.cambiarStatus(id), {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ inc_chStatus: nuevoStatus, observaciones }),
      });
      const data = await manejarRespuesta(response);
      return { success: true, resultado: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const cambiarStatusMasivo = async (ids, nuevoStatus, observaciones = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.incidencia.cambiarStatusMasivo, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ids, inc_chStatus: nuevoStatus, observaciones }),
      });
      const data = await manejarRespuesta(response);
      return { success: true, resultado: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const obtenerTicket = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.incidencia.detallePanel(id), { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, ticket: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // "Guardar cambios" del detalle de un ticket (HU-004 Fase 7) — un solo
  // PATCH con estado + severidad + respuesta pública juntos, en vez de tres
  // llamadas separadas (mismo endpoint que `cambiarStatus`, extendido).
  const guardarTicket = async (id, cambios) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.incidencia.cambiarStatus(id), {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(cambios),
      });
      const data = await manejarRespuesta(response);
      return { success: true, resultado: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const obtenerBitacora = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.historial.historialIncidencia(id), { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, historial: data.historial || [] };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // `filtros`: { fecha_desde, fecha_hasta } — ambos opcionales, sin ellos
  // el backend agrega sobre todo el histórico.
  // Puntos lat/lng para el mapa de calor de Reportes (HU-010 Fase 5) — mismo
  // rango de fechas que `obtenerEstadisticas`, sin `setLoading` global por el
  // mismo motivo que `obtenerMapaHoy` (se pide junto con las estadísticas,
  // no debe pisar ese indicador de carga).
  const obtenerMapaCalor = async ({ fecha_desde = '', fecha_hasta = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (fecha_desde) params.set('fecha_desde', fecha_desde);
      if (fecha_hasta) params.set('fecha_hasta', fecha_hasta);
      const url = params.toString() ? `${API_CIFRA.incidencia.mapaCalor}?${params}` : API_CIFRA.incidencia.mapaCalor;
      const response = await fetch(url, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, puntos: data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const obtenerEstadisticas = async (filtros = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([clave, valor]) => {
        if (valor !== null && valor !== undefined && valor !== '') params.set(clave, valor);
      });
      const url = params.toString() ? `${API_CIFRA.incidencia.estadisticas}?${params}` : API_CIFRA.incidencia.estadisticas;

      const response = await fetch(url, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, estadisticas: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, error,
    obtenerAlertasDelDia, listarIncidencias, cambiarStatus, cambiarStatusMasivo, obtenerEstadisticas,
    obtenerTicket, guardarTicket, obtenerBitacora, obtenerMapaHoy, obtenerMapaCalor,
  };
};

export default useIncidenciasPanel;
